import { Component, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Filter } from '../../interfaces/filter';
import { environment } from 'src/environments/environment';
import { DataService } from '../../services/data.service';
import { StudentDataType } from 'src/app/models/enums/StudentDataType';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ExamAndTaskCalenderItem } from 'src/app/models/ExamsAndTasksCalendar/examAndTaskCalenderItem';
import { FilterItemComponent } from '../filter-item/filter-item.component';
import { toJewishDate, formatJewishDateInHebrew } from "jewish-date";
import { ExamItemComponent } from './exam-item/exam-item.component';
import { FiltersColorHelper } from 'src/app/utils/helpers/filtersColorHelper';
import { LocalizationService } from '../../services/localization.service';
import { add } from 'date-fns';

@Component({
  selector: 'app-exams-list',
  templateUrl: './exams-list.component.html',
  styleUrls: ['./exams-list.component.scss', './exams-list.component.en.scss']
})
export class ExamsListComponent implements OnInit {
  constructor(private dataService: DataService) { 
    this.searchTermObj
      .pipe(
        debounceTime(1000)  // Waits in milliseconds after the last keystroke
      )
      .subscribe(term => {
        this.performSearch(term);
      });
  }

  @ViewChild('periodFilter') periodFilterComponent!: FilterItemComponent;
  @ViewChild('typeFilter') typeFilterComponent!: FilterItemComponent;
  @ViewChildren(ExamItemComponent) examItemsComponents!: QueryList<ExamItemComponent>;

  @Input() blockData: any;
  examsList: ExamAndTaskCalenderItem[] = [];
  selectedTypes: StudentDataType[] = [
    StudentDataType.Exam,
    StudentDataType.Quiz,
    StudentDataType.Assignment,
    StudentDataType.Lesson,
    StudentDataType.Event,
  ];
  searchTermObj = new Subject<string>();
  searchTerm: string | null = null;
  isLoading: boolean = true;

  periodFilterItems: Filter[] = [];
  selectPeriodFilterItem!: Filter;

  FiltersColorHelper = FiltersColorHelper;
  LocalizationService = LocalizationService;

  typeFilterItems: Filter[] = [
    {
      value: 'assignments',
      valueArr: [StudentDataType.Assignment],
      label: 'מטלות',
      color: FiltersColorHelper.defaultColorsList.assignments.primary,
      checked: true
    },
    {
      value: 'exams and quizzes',
      valueArr: [StudentDataType.Exam, StudentDataType.Quiz],
      label: 'בחינות וחידונים',
      color: FiltersColorHelper.defaultColorsList.exams.primary,
      checked: true
    },
    {
      value: 'lessons',
      valueArr: [StudentDataType.Lesson],
      label: 'שיעורים',
      color: FiltersColorHelper.defaultColorsList.lessons.primary,
      checked: true
    },
    {
      value: 'academic events',
      valueArr: [StudentDataType.Event],
      label: 'אירועים אקדמיים',
      color: FiltersColorHelper.defaultColorsList.academicEvents.primary,
      checked: true
    }
  ];

  isServerError: boolean = false;

  ngOnInit(): void {
    let firstSelect = Math.min(...this.blockData.datePeriodSelectOptions.map((obj: any) => obj.id));
    this.periodFilterItems = this.blockData.datePeriodSelectOptions.map((p: any) => {
      return {
        value: p.id,
        label: p.subName ? p.name + ' (' + p.subName + ')' : p.name,
        checked: p.id == firstSelect ? true : false
      };
    });
    this.selectPeriodFilterItem = this.periodFilterItems.find(c => c.checked)!;

    if(this.blockData.studentCalendarFilters) {
      this.typeFilterItems = FiltersColorHelper.convertToFilterArr(this.blockData.studentCalendarFilters);
      this.selectedTypes = FiltersColorHelper.convertStudentDataTypeToArr().filter((item) => 
        FiltersColorHelper.convertToCheckboxEnumArr(this.typeFilterItems).includes(item));
    }

    this.getData();
  }

  getData() {
    this.isLoading = true;
    let url =
      environment.apiUrl +
      '/Portal/StudentExamsAndTasksListViewBlockList/SearchExamsAndTasks';
    let rangeDates = this.getDisplayRangeDate();
    var request: any = {
      cultureCode: this.dataService.getCultureCode(),
      dataTypes: this.selectedTypes,
      fromDate: rangeDates[0],
      toDate: rangeDates[1],
      searchOneDay: false,
      term: this.searchTerm,
      showWarningBubblePeriod: this.blockData.showEventBubblePeriod
    };
    this.dataService.getDataByToken(url, request).subscribe({
      next: (responseData) => {
        this.examsList = [];
        if (responseData != null && responseData.length > 0) {
          let list: ExamAndTaskCalenderItem[] = responseData.map(
            (item: any) => item as ExamAndTaskCalenderItem
          );
          list.sort((a, b) => {
            let dateA = new Date(a.date);
            let dateB = new Date(b.date);
            return dateA.getTime() - dateB.getTime(); 
          });
          this.examsList = list;
          //console.log(this.examsList);
        }
        this.isLoading = false;
      },
      error: (e: any) => {
        console.error(e);
        this.isLoading = false;
        this.isServerError = true;
      }
    });
  }

  getDisplayRangeDate(): [Date, Date] {
    let filter = this.blockData.datePeriodSelectOptions.find((c: any) => c.id == this.selectPeriodFilterItem.value);
    const timeZoneOffset = new Date().getTimezoneOffset();
    let result: [Date, Date] = [add(new Date(filter.startDate), { minutes: -timeZoneOffset }), add(new Date(filter.endDate), { minutes: -timeZoneOffset })]
    return result;
  }

  onKeySearch(event: any) {
    this.searchTermObj.next(event.target.value);
  }

  performSearch(term: string) {
    this.searchTerm = term == '' ? null : term;
    this.getData();
  }

  periodChange(type: any) {
    this.periodFilterItems.forEach(c => { c.checked = false; });
    let index = this.periodFilterItems.findIndex(c => c.value == type);
    this.periodFilterItems[index].checked = true;
    this.selectPeriodFilterItem = this.periodFilterItems[index];
    this.periodFilterComponent.initValue();
    this.getData();
  }

  mobilePeriodChange(filters: Filter[]) {
    this.periodFilterItems = filters;
    this.selectPeriodFilterItem = this.periodFilterItems.find(c => c.checked)!;
    this.getData();
  }

  typeFilterChange(event: any, value: string, types?: any[]) {
    let index = this.typeFilterItems.findIndex(c => c.value == value);
    this.typeFilterItems[index].checked = event.checked;

    let checkboxEnums = types!.map(Number).filter((val: number) => Object.values(StudentDataType).includes(val)) as StudentDataType[];
    if(event.checked) {
      this.selectedTypes.push(...checkboxEnums);
    } else {
      this.selectedTypes = this.selectedTypes.filter((item) => !checkboxEnums.includes(item));
    }

    this.typeFilterComponent.initValue();
    this.getData();
  }

  mobileTypeFilterChange(filters: Filter[]) {
    this.typeFilterItems = filters;
    let checkboxEnums = filters.filter(c => c.checked).reduce<StudentDataType[]>((acc, filter) => [...acc, ...filter.valueArr!], []);
    let enumToArray: StudentDataType[] = Object.keys(StudentDataType).filter((key) => isNaN(Number(key)))
      .map(key => StudentDataType[key as any]) as unknown as StudentDataType[];
    this.selectedTypes = enumToArray.filter((item) => checkboxEnums.includes(item));
    this.getData();
  }

  getHebrewDate(date: Date): string {
    let jewishDate = toJewishDate(date);
    return formatJewishDateInHebrew(jewishDate);
  }

  closeAllActivePopups() {
    this.examItemsComponents.forEach((component) => {
      component.closeEventPopup();
    });
  }

  getNoResultsText(): string {
    let addText = this.searchTerm ? '<strong>' + this.searchTerm + '</strong>' : '';
    let text = this.blockData.noResultsText ? this.blockData.noResultsText.replace('{searchText}', addText) : addText;
    return '<span>' + text +'</span>';
  }
}
